import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { getTaskStatusLabel, isTaskResolved } from "./TaskQueueStore";
import { useStatusPalette } from "../../../utils/statusColors";
import CountdownChip from "../../common/CountdownChip";

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.08); }
  50% { box-shadow: 0 0 12px 2px rgba(59,130,246,0.18); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.7; }
`;

export const STATUS_DOT = {
  DONE: { bg: "#22c55e", border: "#22c55e", shadow: "none" },
  IN_PROGRESS: { bg: "#3b82f6", border: "#3b82f6", shadow: "0 0 0 3px rgba(59,130,246,0.15)" },
  BLOCKED: { bg: "#f59e0b", border: "#f59e0b", shadow: "none" },
  FAILED: { bg: "#ef4444", border: "#ef4444", shadow: "none" },
  SKIPPED: { bg: "#8b5cf6", border: "#8b5cf6", shadow: "none" },
  CANCELLED: { bg: "#9ca3af", border: "#9ca3af", shadow: "none" },
  PENDING: { bg: "transparent", border: "#d1d5db", shadow: "none" },
};

function formatCardTimestamp(value) {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Amber live countdown, shown only while a time-bound task is still open. */
export function TaskDeadlineChip({ task }) {
  if (!task?.dueAt || isTaskResolved(task?.status)) return null;
  return (
    <Box sx={{ display: "flex", mt: 0.75, pl: "22px" }}>
      <CountdownChip targetAt={task.dueAt} variant="amber" pulse />
    </Box>
  );
}

// The list and kanban cards are the same card at two densities; only text sizing, the
// title wrap mode, the subtitle content, the hover border, and the footer differ.
const DENSITY = {
  list: {
    titleSx: {
      fontSize: "13px",
      lineHeight: 1.4,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    subtitleSx: { fontSize: "11px" },
    hoverBorder: "text.disabled",
  },
  kanban: {
    titleSx: {
      fontSize: "12px",
      lineHeight: 1.3,
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    subtitleSx: { fontSize: "10px" },
    hoverBorder: "divider",
  },
};

export default function TaskCard({ task, selected, onClick, index, density = "list" }) {
  const _sp = useStatusPalette();
  const cfg = DENSITY[density] || DENSITY.list;
  const status = String(task?.status || "").toUpperCase();
  const dot = STATUS_DOT[status] || STATUS_DOT.PENDING;
  const isActive = status === "IN_PROGRESS";
  const statusLabel = getTaskStatusLabel(status);
  const attempts = Number(task?.attemptCount || 0);
  const prefix = typeof index === "number" ? `${index + 1}. ` : "";
  const subtitle =
    density === "kanban" ? `${statusLabel} · Attempt ${attempts}` : statusLabel;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: isActive || selected ? _sp.blue.border : "divider",
        backgroundColor: isActive || selected ? _sp.blue.bg : "background.paper",
        cursor: "pointer",
        transition: "all 150ms ease",
        boxShadow: selected ? "0 0 0 1px rgba(59,130,246,0.12)" : "0 1px 2px rgba(0,0,0,0.04)",
        ...(isActive && { animation: `${pulseGlow} 2.4s ease-in-out infinite` }),
        "&:hover": {
          borderColor: selected || isActive ? _sp.blue.border : cfg.hoverBorder,
          backgroundColor: selected || isActive ? _sp.blue.bg : "action.hover",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: `2px solid ${dot.border}`,
            backgroundColor: dot.bg,
            boxShadow: dot.shadow,
            flexShrink: 0,
            ...(isActive && { animation: `${pulseDot} 2.4s ease-in-out infinite` }),
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "text.primary", ...cfg.titleSx }}>
            {prefix}
            {task?.title || "Untitled task"}
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.25, ...cfg.subtitleSx }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <TaskDeadlineChip task={task} />

      {density === "list" ? (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, pl: "22px" }}>
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            Attempts: {attempts}
          </Typography>
          {task?.updatedAt && (
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              {formatCardTimestamp(task.updatedAt)}
            </Typography>
          )}
        </Box>
      ) : (
        task?.updatedAt && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, pl: "22px" }}>
            <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
              {formatCardTimestamp(task.updatedAt)}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
}
